import { useEffect } from 'react';
import { SIGN_IN_VIEW } from 'common/constants/auth';
import { signInViewRequiresIdentifier, signInViewRequiresSession } from 'pages/Auth/SignIn/SignIn.utils';
import type { UseSignInRouteGuardsParams } from './useSignInRouteGuards.types';

const useSignInRouteGuards = ({
  accessToken,
  currentUser,
  identifier,
  mode,
  normalizedRouteState,
  product,
  returnTo,
  source,
  updateRouteState,
  verifyAccessToken,
  view,
}: UseSignInRouteGuardsParams) => {
  // Normalize stale auth route params without adding extra history entries during multi-step sign-in transitions.
  useEffect(() => {
    const hasRouteStateDrift = (
      mode !== normalizedRouteState.mode
      || product !== normalizedRouteState.product
      || returnTo !== normalizedRouteState.returnTo
      || source !== normalizedRouteState.source
    );

    if (!hasRouteStateDrift) {
      return;
    }

    updateRouteState(normalizedRouteState);
  }, [
    mode,
    normalizedRouteState,
    product,
    returnTo,
    source,
    updateRouteState,
  ]);

  // Password and session-bound steps cannot survive refresh unless the required local/session state still exists.
  useEffect(() => {
    // The password step depends on a local-only identifier, so refresh intentionally falls back to the first step.
    if (signInViewRequiresIdentifier(view) && !identifier) {
      updateRouteState({ mode: null, product: null, view: SIGN_IN_VIEW.IDENTIFIER });
      return;
    }

    if (signInViewRequiresSession(view) && !accessToken && !currentUser) {
      updateRouteState({ mode: null, product: null, view: SIGN_IN_VIEW.IDENTIFIER });
    }
  }, [accessToken, currentUser, identifier, updateRouteState, view]);

  useEffect(() => {
    if (accessToken && !currentUser) {
      verifyAccessToken();
    }
  }, [accessToken, currentUser, verifyAccessToken]);

  // The 2FA step can legitimately open print/download surfaces while backup codes are being acknowledged.
  // Revalidating the token on that focus bounce resets the in-progress OTP handoff back to verify.
  useEffect(() => {
    if (!signInViewRequiresSession(view) || !accessToken || view === SIGN_IN_VIEW.TWO_FACTOR) {
      return undefined;
    }

    verifyAccessToken();

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      verifyAccessToken();
    };

    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    return () => {
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, [accessToken, verifyAccessToken, view]);
};

export default useSignInRouteGuards;
